/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Button, Drawer, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, queryCache } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ReviewForm } from './ReviewForm';
import { ReviewList } from './ReviewList';
import { ReviewCard } from './ReviewCard';
import { ReviewDashboard } from './ReviewDashboard';
import type {
  BinaryReview,
  StarReview,
  ReviewConfig,
  ReviewServiceFunctions,
} from './types';

type ReviewDrawerProps = {
  visible: boolean;
  onClose: () => void;
  entityId: number;
  entityName?: string;
  contextIds: Record<string, number | string>;
  config: ReviewConfig;
  reviewService: ReviewServiceFunctions;
  phase?: 'screening' | 'evaluation';
};

export const ReviewDrawer: React.FC<ReviewDrawerProps> = ({
  visible,
  onClose,
  entityId,
  entityName,
  contextIds,
  config,
  reviewService,
  phase,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [showForm, setShowForm] = useState(false);

  const isBinary = config.ratingType === 'binary';

  // Fetch dashboard independently
  const dashboardQueryKey = [
    `${config.queryKeyPrefix}-dashboard`,
    ...Object.values(contextIds),
    entityId,
  ];

  const { data: dashboardData } = useQuery(
    dashboardQueryKey,
    () => {
      if (reviewService.getDashboard) {
        return reviewService.getDashboard({ entityId });
      }
      return null;
    },
    {
      enabled: !!visible && !!entityId && !!reviewService.getDashboard,
    }
  );

  // Patch only the affected offer's review dashboard inside the cached offers
  // lists instead of invalidating (refetching) the whole list. This keeps the
  // offers list from fully re-rendering/flashing when a review changes.
  const syncOfferDashboardToLists = async () => {
    const { projectId, tenderId, contractId } = contextIds;

    const listKeys = isBinary
      ? [['contract-screening-offers', projectId, tenderId, contractId]]
      : [
          ['contract-evaluation-offers', projectId, tenderId, contractId],
          ['contract-result-offers', projectId, tenderId, contractId],
        ];

    const hasCachedList = listKeys.some((key) =>
      Array.isArray(queryCache.getQueryData(key))
    );

    if (!reviewService.getDashboard || !hasCachedList) {
      return;
    }

    let freshDashboard: any = null;
    try {
      freshDashboard = await reviewService.getDashboard({ entityId });
    } catch {
      // Fall back to invalidation so the row data still stays correct.
      listKeys.forEach((key) => queryCache.invalidateQueries(key));
      return;
    }

    const applyDashboard = (offer: any) => {
      if (!offer || offer.id !== entityId) return offer;

      if (isBinary) {
        return {
          ...offer,
          screening: {
            ...offer.screening,
            screening_binary_review_dashboard: freshDashboard,
          },
        };
      }

      return {
        ...offer,
        evaluation: {
          ...offer.evaluation,
          evaluation_star_review_dashboard: freshDashboard,
        },
      };
    };

    listKeys.forEach((key) => {
      const existing = queryCache.getQueryData(key);
      if (!Array.isArray(existing)) return;
      queryCache.setQueryData(key, existing.map(applyDashboard));
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    queryCache.invalidateQueries(dashboardQueryKey);
    // Invalidate the review list queries so new review appears immediately
    queryCache.invalidateQueries([config.queryKeyPrefix]);
    // Surgically refresh the row's dashboard without refetching the list
    syncOfferDashboardToLists();
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const handleCommentsChange = () => {
    queryCache.invalidateQueries(dashboardQueryKey);
    // Invalidate the review list queries so comments appear immediately
    queryCache.invalidateQueries([config.queryKeyPrefix]);
    syncOfferDashboardToLists();
  };

  const drawerTitle = entityName
    ? intl.formatMessage(
        { id: 'review-drawer-title-with-entity' },
        { entityName }
      )
    : intl.formatMessage({ id: 'review-drawer-title' });

  // Determine phase for ReviewCard compatibility if not provided
  const reviewPhase = phase || (isBinary ? 'screening' : 'evaluation');

  return (
    <Drawer
      title={drawerTitle}
      visible={visible}
      onClose={onClose}
      width={720}
      css={styles.drawer}
      bodyStyle={{ padding: '24px' }}>
      <div css={styles.content}>
        {/* Dashboard Section - ALWAYS ON TOP */}
        {dashboardData &&
          dashboardData.total_reviews > 0 &&
          reviewPhase &&
          !showForm && (
            <ReviewDashboard
              dashboardData={dashboardData}
              phase={reviewPhase}
            />
          )}

        {/* Add/Edit Review Section */}
        {showForm ? (
          <ReviewForm
            entityId={entityId}
            contextIds={contextIds}
            config={config}
            upsertReview={reviewService.upsertReview}
            existingReview={null}
            onSubmitSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        ) : !config.isReadOnly ? (
          <div css={styles.addReviewSection}>
            <Button
              type="primary"
              icon={<PlusOutlined rev={''} />}
              onClick={() => setShowForm(true)}
              size="large"
              css={styles.addButton}>
              {intl.formatMessage({ id: 'review-add-edit-review-button' })}
            </Button>
          </div>
        ) : null}

        {/* Divider */}
        {(showForm || (dashboardData && dashboardData.total_reviews > 0)) && (
          <div css={styles.divider} />
        )}

        {/* All Reviews List */}
        {!showForm && (
          <div css={styles.reviewsSection}>
            <ReviewList
              entityId={entityId}
              contextIds={contextIds}
              config={config}
              getReviews={reviewService.getReviews}
              enableComments={config.enableComments}
              reviewService={reviewService}
              onCommentsChange={handleCommentsChange}
              isReadOnly={config.isReadOnly}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

const styleFn: ThemedStyleFn = () => ({
  drawer: {
    '.ant-drawer-header': {
      borderBottom: '1px solid #f0f0f0',
    },
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },

  addReviewSection: {
    marginBottom: 24,
  },

  addButton: {
    width: '100%',
    height: 48,
  },

  divider: {
    height: 1,
    background: '#f0f0f0',
    marginBottom: 24,
  },

  reviewsSection: {
    flex: 1,
  },
});
